/**
 * Income-tax regime elections, investment declarations, and the proofs that support them.
 *
 * TWO AUDIENCES, ONE CONTRACT, AND THE SPLIT MATTERS. An employee maintains their own declaration
 * (`payroll.tax.own:manage`, self scope). HR verifies other people's proofs
 * (`payroll.tax.proof:verify`, entity or org scope). Nothing here is reachable by holding
 * `payroll.run:approve`: approving a payroll run is not a reason to read somebody's rent
 * agreement, and `can()` refuses `payroll.tax.proof:verify` against the caller's own record, so an
 * HR admin cannot be both sides of their own review.
 *
 * FOUR AMOUNTS THAT ARE NOT THE SAME NUMBER. `declaredAmount` is the claim. `evidenceAmount` is
 * what the documents add up to. `verifiedAmount` is what the reviewer accepted. The DEDUCTION is
 * none of those: it is what tax law allows once the category cap, the section's aggregate cap and
 * the elected regime have been applied, it depends on the other items in the same declaration
 * (80C is one ceiling shared by several categories), and it is computed by `@hrms/payroll` at
 * calculation time. A verified receipt is evidence, not an entitlement.
 *
 * RULES COME FROM CONFIGURATION, NOT FROM CODE. Categories, caps, rates, slabs and the submission
 * window all live in a reviewed, versioned `PayrollTaxYearConfig` per legal entity and fiscal year.
 * A screen that needs to know when declarations close reads it from `taxYearSchema`; there is no
 * hardcoded deadline and no hardcoded ceiling anywhere in this module.
 *
 * REVISIONS, NEVER EDITS. A submitted declaration is immutable. A correction opens a new revision
 * and the previous one keeps its items and its reviews exactly as the reviewer saw them.
 *
 * PROOFS ARE PRIVATE. There is no URL on a proof anywhere in this contract. Uploading is a
 * three-step handshake (authorise, PUT, confirm) and every read mints a link that expires in
 * minutes, after re-checking `can()` against the owning employment.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  CURRENCIES,
  TAX_CATEGORY_CALCULATIONS,
  TAX_PAYROLL_SOURCES,
  TAX_DECLARATION_STATES,
  TAX_PROOF_DECISIONS,
  TAX_REGIMES,
} from '../enums.js';
import {
  dateOnlySchema,
  decimalStringSchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

export const taxRegimeSchema = z.enum(TAX_REGIMES);
export const taxDeclarationStateSchema = z.enum(TAX_DECLARATION_STATES);
export const taxProofDecisionSchema = z.enum(TAX_PROOF_DECISIONS);
export const taxCategoryCalculationSchema = z.enum(TAX_CATEGORY_CALCULATIONS);
export const taxPayrollSourceSchema = z.enum(TAX_PAYROLL_SOURCES);
/** Local alias — `currencySchema` itself is exported from schemas/common.js. */
const currency = z.enum(CURRENCIES);

/** A non-negative money amount. Declarations never carry a negative claim. */
export const claimAmountSchema = decimalStringSchema.refine((v) => !v.startsWith('-'), {
  message: 'Amount cannot be negative',
});

/** A fiscal year named by the calendar year it starts in: FY 2024-25 is 2024. */
export const fiscalYearSchema = z.number().int().min(2000).max(2100);

// ---------------------------------------------------------------------------
// Tax-year configuration
// ---------------------------------------------------------------------------

/**
 * One declaration category an employee may claim under, as configured for a fiscal year.
 *
 * `cap` is the category's OWN ceiling; `sectionCode` is what it aggregates into, and the section's
 * shared ceiling lives on `taxSectionSchema` below. Both are needed: a health-insurance premium for
 * parents has its own limit AND counts towards 80D as a whole.
 */
export const taxCategorySchema = z.object({
  code: z.string().min(1).max(60),
  label: z.string().min(1).max(120),
  sectionCode: z.string().min(1).max(20),
  /** How the claimable amount is derived — decides which of `inputs` the engine reads. */
  calculation: taxCategoryCalculationSchema,
  /**
   * Only with `calculation: 'payroll_sourced'`: the payroll figure that IS the amount (employee PF for 80C, professional
   * tax for 16(iii)). Such a category cannot be declared; absent or null, a payroll-sourced category is declared as before.
   */
  payrollSource: taxPayrollSourceSchema.nullable().optional(),
  /** The category's own annual ceiling, or null when only the section's cap applies. */
  cap: claimAmountSchema.nullable(),
  /** Which regimes allow it at all. Most Chapter VI-A deductions are old-regime only. */
  allowedRegimes: z.array(taxRegimeSchema).min(1),
  /** Whether a document is required before the claim can be verified. */
  proofRequired: z.boolean(),
  /** Shown on the declaration form — what the employee is expected to attach. */
  proofGuidance: z.string().max(300).nullable(),
  isActive: z.boolean().default(true),
});
export type TaxCategory = z.infer<typeof taxCategorySchema>;

export const taxSectionSchema = z.object({
  code: z.string().min(1).max(20),
  label: z.string().min(1).max(120),
  /** The ceiling shared by every category in this section, or null for no aggregate cap. */
  aggregateCap: claimAmountSchema.nullable(),
  allowedRegimes: z.array(taxRegimeSchema).min(1),
});
export type TaxSection = z.infer<typeof taxSectionSchema>;

/** One income-tax slab. `upTo` null is the top, open-ended slab. */
export const taxSlabSchema = z.object({
  upTo: claimAmountSchema.nullable(),
  /** A percentage, as a decimal string: "5", "20", "30". */
  ratePercent: decimalStringSchema,
});

export const taxRegimeRulesSchema = z.object({
  regime: taxRegimeSchema,
  slabs: z.array(taxSlabSchema).min(1),
  standardDeduction: claimAmountSchema,
  /** Rebate under section 87A: the ceiling on taxable income and the maximum rebate. */
  rebateIncomeLimit: claimAmountSchema.nullable(),
  rebateMaxAmount: claimAmountSchema.nullable(),
  /**
   * Marginal relief on the 87A rebate (the proviso for the new regime): just above `rebateIncomeLimit`, the tax cannot exceed
   * the income above the limit. Omitted: on for the new regime, off for the old one, which has no such proviso.
   */
  rebateMarginalRelief: z.boolean().optional(),
  /** Health and education cess, as a percentage of tax plus surcharge. */
  cessPercent: decimalStringSchema,
  /** Surcharge bands, applied to tax before cess. */
  surcharge: z
    .array(z.object({ aboveIncome: claimAmountSchema, ratePercent: decimalStringSchema }))
    .default([]),
});
export type TaxRegimeRules = z.infer<typeof taxRegimeRulesSchema>;

/**
 * The reviewed body of a tax year's rules. Written only by `payroll.tax.config:manage`, parsed
 * before it is stored, and read by the engine — which is why the engine holds no constants.
 */
export const taxYearConfigSchema = z.object({
  sections: z.array(taxSectionSchema).min(1),
  categories: z.array(taxCategorySchema).min(1),
  regimes: z.array(taxRegimeRulesSchema).min(1),
  /** The regime applied when an employee never elected one. */
  defaultRegime: taxRegimeSchema,
  /** HRA: the exemption rate for metro vs non-metro cities, as percentages of salary. */
  hraMetroPercent: decimalStringSchema,
  hraNonMetroPercent: decimalStringSchema,
  /** The share of salary deducted from rent paid in the 10(13A) computation. */
  hraRentSalaryPercent: decimalStringSchema,
  /** Section 24(b) ceiling for a self-occupied property. */
  homeLoanSelfOccupiedCap: claimAmountSchema,
});
export type TaxYearConfig = z.infer<typeof taxYearConfigSchema>;

export const taxYearSchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  legalEntityName: z.string(),
  fiscalYear: fiscalYearSchema,
  /** "FY 2024-25". Derived from the entity's fiscal-year start, never assembled on the client. */
  fiscalYearLabel: z.string(),
  countryCode: z.string().length(2),
  versionNo: z.number().int().positive(),
  status: z.enum(['draft', 'published', 'archived']),
  config: taxYearConfigSchema,
  submissionOpensOn: dateOnlySchema,
  submissionClosesOn: dateOnlySchema,
  proofSubmissionClosesOn: dateOnlySchema,
  /**
   * Whole days from today (in the ENTITY's timezone) until declarations close. Negative once it has
   * passed. Computed server-side so the countdown cannot disagree with the deadline the rules are
   * enforced against.
   */
  daysUntilSubmissionCloses: z.number().int(),
  isSubmissionOpen: z.boolean(),
  isProofWindowOpen: z.boolean(),
  currency: currency,
  note: z.string().nullable(),
  publishedAt: instantSchema.nullable(),
  publishedByName: z.string().nullable(),
});
export type TaxYear = z.infer<typeof taxYearSchema>;

export const upsertTaxYearBodySchema = z
  .object({
    legalEntityId: uuidSchema,
    fiscalYear: fiscalYearSchema,
    config: taxYearConfigSchema,
    submissionOpensOn: dateOnlySchema,
    submissionClosesOn: dateOnlySchema,
    proofSubmissionClosesOn: dateOnlySchema,
    note: z.string().trim().max(500).nullable().optional(),
    /** Publish immediately, or leave it as a draft for review. */
    publish: z.boolean().default(false),
  })
  .refine((b) => b.submissionOpensOn <= b.submissionClosesOn, {
    message: 'The submission window cannot close before it opens',
    path: ['submissionClosesOn'],
  })
  .refine((b) => b.submissionClosesOn <= b.proofSubmissionClosesOn, {
    message: 'Proofs cannot stop being accepted before declarations close',
    path: ['proofSubmissionClosesOn'],
  })
  .refine(
    (b) =>
      b.config.categories.every((cat) => b.config.sections.some((s) => s.code === cat.sectionCode)),
    {
      message: 'Every category must roll up to a section defined in the same configuration',
      path: ['config', 'categories'],
    },
  )
  .refine((b) => b.config.regimes.some((r) => r.regime === b.config.defaultRegime), {
    message: 'The default regime must be one of the configured regimes',
    path: ['config', 'defaultRegime'],
  });

// ---------------------------------------------------------------------------
// Regime election
// ---------------------------------------------------------------------------

export const taxElectionSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  fiscalYear: fiscalYearSchema,
  regime: taxRegimeSchema,
  revisionNo: z.number().int().positive(),
  /** employee | hr | default */
  source: z.enum(['employee', 'hr', 'default']),
  reason: z.string().nullable(),
  electedByName: z.string().nullable(),
  electedAt: instantSchema,
  /** Set once a payroll run has consumed this election. The election may still change. */
  lockedAt: instantSchema.nullable(),
  isCurrent: z.boolean(),
});
export type TaxElection = z.infer<typeof taxElectionSchema>;

/**
 * A regime comparison for one employee, so the election is an informed one.
 *
 * ESTIMATE, AND LABELLED AS ONE. It is computed from what is known today — projected annual salary
 * and the CURRENT declaration's verified-or-declared figures — and the year is not over. The real
 * liability is whatever the payroll engine computes each month against verified proofs.
 */
export const regimeComparisonSchema = z.object({
  fiscalYear: fiscalYearSchema,
  currency: currency,
  projectedAnnualGross: decimalStringSchema,
  /** Which figures fed the estimate, so the screen can say so plainly. */
  basis: z.enum(['verified_amounts', 'declared_amounts', 'no_declaration']),
  options: z.array(
    z.object({
      regime: taxRegimeSchema,
      taxableIncome: decimalStringSchema,
      totalDeductions: decimalStringSchema,
      estimatedAnnualTax: decimalStringSchema,
      isCurrentElection: z.boolean(),
    }),
  ),
  /** The lower estimate, or null when the two are equal. */
  lowerRegime: taxRegimeSchema.nullable(),
  estimatedSaving: decimalStringSchema.nullable(),
});
export type RegimeComparison = z.infer<typeof regimeComparisonSchema>;

export const electRegimeBodySchema = z.object({
  fiscalYear: fiscalYearSchema,
  regime: taxRegimeSchema,
  /** Required when HR elects on someone's behalf; optional for an employee's own choice. */
  reason: z.string().trim().max(300).nullable().optional(),
});

// ---------------------------------------------------------------------------
// Declarations
// ---------------------------------------------------------------------------

/**
 * A category's own fields. Every branch is optional at this level and REQUIRED per calculation by
 * `declarationItemInputSchema`'s refinements below — a shared object keeps one storage shape while
 * the rules stay strict.
 */
export const declarationInputsSchema = z.object({
  /** hra_exemption */
  landlordName: z.string().trim().max(120).optional(),
  /** Redacted out of the audit log by audit_redact's nested pass — see prisma/sql/audit.sql. */
  landlordPan: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/, 'Not a valid PAN')
    .optional(),
  rentPropertyAddress: z.string().trim().max(300).optional(),
  monthlyRent: claimAmountSchema.optional(),
  rentFromMonth: dateOnlySchema.optional(),
  rentToMonth: dateOnlySchema.optional(),
  isMetroCity: z.boolean().optional(),
  /** home_loan_interest */
  lenderName: z.string().trim().max(120).optional(),
  loanAccountNumber: z.string().trim().max(60).optional(),
  propertyAddress: z.string().trim().max(300).optional(),
  isSelfOccupied: z.boolean().optional(),
  /** declared_amount — an insurer, a policy, a school, whatever the category asks for. */
  providerName: z.string().trim().max(120).optional(),
  policyOrReference: z.string().trim().max(80).optional(),
  /** Free-form context for the reviewer. */
  employeeNote: z.string().trim().max(500).optional(),
});
export type DeclarationInputs = z.infer<typeof declarationInputsSchema>;

export const declarationItemInputSchema = z
  .object({
    categoryCode: z.string().min(1).max(60),
    declaredAmount: claimAmountSchema,
    inputs: declarationInputsSchema.default({}),
  })
  .superRefine((item, ctx) => {
    if (item.declaredAmount === '0' || Number.parseFloat(item.declaredAmount) === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Remove the category rather than declaring zero',
        path: ['declaredAmount'],
      });
    }
  });
export type DeclarationItemInput = z.infer<typeof declarationItemInputSchema>;

export const declarationProofSchema = z.object({
  id: uuidSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  /** pending until the server has confirmed the object is really stored. */
  uploadState: z.enum(['pending', 'stored', 'rejected']),
  rejectionReason: z.string().nullable(),
  statedAmount: decimalStringSchema.nullable(),
  uploadedAt: instantSchema,
  uploadedByName: z.string().nullable(),
  /**
   * NO URL. A link is minted per request by `getProofLink`, expires in minutes, and is issued only
   * after `can()` has been re-checked against the owning employment.
   */
});
export type DeclarationProof = z.infer<typeof declarationProofSchema>;

export const declarationItemSchema = z.object({
  id: uuidSchema,
  categoryCode: z.string(),
  categoryLabel: z.string(),
  sectionCode: z.string(),
  sectionLabel: z.string(),
  calculation: taxCategoryCalculationSchema,
  declaredAmount: decimalStringSchema,
  evidenceAmount: decimalStringSchema.nullable(),
  verifiedAmount: decimalStringSchema.nullable(),
  currency: currency,
  inputs: declarationInputsSchema,
  state: z.enum([
    'submitted',
    'verified',
    'partially_verified',
    'disallowed',
    'clarification_required',
  ]),
  proofRequired: z.boolean(),
  proofGuidance: z.string().nullable(),
  proofs: z.array(declarationProofSchema),
  /** The latest review on this item, if any. Earlier ones are on the declaration's history. */
  latestReview: z
    .object({
      decision: taxProofDecisionSchema,
      acceptedAmount: decimalStringSchema,
      remarks: z.string().nullable(),
      reviewedByName: z.string().nullable(),
      reviewedAt: instantSchema,
    })
    .nullable(),
  version: z.number().int().positive(),
});
export type DeclarationItem = z.infer<typeof declarationItemSchema>;

export const declarationSchema = z.object({
  id: uuidSchema,
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
  legalEntityId: uuidSchema,
  fiscalYear: fiscalYearSchema,
  fiscalYearLabel: z.string(),
  revisionNo: z.number().int().positive(),
  isCurrent: z.boolean(),
  regime: taxRegimeSchema,
  state: taxDeclarationStateSchema,
  totalDeclared: decimalStringSchema,
  totalEvidenced: decimalStringSchema,
  totalVerified: decimalStringSchema,
  currency: currency,
  items: z.array(declarationItemSchema),
  submittedAt: instantSchema.nullable(),
  reviewCompletedAt: instantSchema.nullable(),
  /**
   * What the tax rules ALLOW on the verified amounts, per section — the deduction, as distinct from
   * the three amounts above. Computed, never stored, and null until something has been verified.
   */
  eligibleDeductions: z
    .array(
      z.object({
        sectionCode: z.string(),
        sectionLabel: z.string(),
        claimedBasis: decimalStringSchema,
        aggregateCap: decimalStringSchema.nullable(),
        eligibleAmount: decimalStringSchema,
        /** Why it was cut down, when it was: the category cap, the section cap, or the regime. */
        limitedBy: z.enum(['category_cap', 'section_cap', 'regime', 'none']),
      }),
    )
    .nullable(),
  version: z.number().int().positive(),
});
export type Declaration = z.infer<typeof declarationSchema>;

/**
 * The employee's own view: the window, their election, their current revision, and the categories
 * they may claim under. One request, because the screen is useless with any part of it missing.
 */
export const myDeclarationSchema = z.object({
  employmentId: uuidSchema,
  taxYear: taxYearSchema,
  election: taxElectionSchema.nullable(),
  electionHistory: z.array(taxElectionSchema),
  declaration: declarationSchema.nullable(),
  /** Earlier revisions, newest first — an employee can see what they previously submitted. */
  previousRevisions: z.array(
    z.object({
      id: uuidSchema,
      revisionNo: z.number().int().positive(),
      state: taxDeclarationStateSchema,
      totalDeclared: decimalStringSchema,
      totalVerified: decimalStringSchema,
      submittedAt: instantSchema.nullable(),
    }),
  ),
  /** Filtered to the elected regime — a category the regime disallows is not offered. */
  availableCategories: z.array(taxCategorySchema),
  canEdit: z.boolean(),
  canSubmit: z.boolean(),
  /** Why not, when `canSubmit` is false: "the window closed", "nothing declared". */
  submitBlockedReason: z.string().nullable(),
});
export type MyDeclaration = z.infer<typeof myDeclarationSchema>;

/**
 * Save the working revision. WHOLE-LIST, NOT PER-ITEM, deliberately: 80C is one shared ceiling, so
 * the server has to see every item together to tell the employee what is actually claimable. A
 * per-item PATCH would make that answer depend on the order the client happened to save in.
 */
export const saveDeclarationBodySchema = z.object({
  fiscalYear: fiscalYearSchema,
  /** Rejects a save against a revision someone else (or another tab) has already moved on. */
  expectedVersion: z.number().int().positive().nullable(),
  items: z.array(declarationItemInputSchema).max(60),
});

export const submitDeclarationBodySchema = z.object({
  fiscalYear: fiscalYearSchema,
  expectedVersion: z.number().int().positive(),
});

/** Opens a NEW revision from the submitted one, for the employee to correct. */
export const reviseDeclarationBodySchema = z.object({
  fiscalYear: fiscalYearSchema,
  reason: z.string().trim().min(1).max(300),
});

// ---------------------------------------------------------------------------
// Proof upload — a three-step handshake
// ---------------------------------------------------------------------------

/**
 * STEP 1. The server authorises exactly one object: it checks the caller owns the declaration, that
 * the proof window is open, that the declared type and size are allowed, and only then mints a
 * short-lived PUT URL for a key IT chose. The client never names a key.
 */
export const requestProofUploadBodySchema = z.object({
  declarationItemId: uuidSchema,
  fileName: z.string().trim().min(1).max(200),
  mimeType: z.string().trim().min(1).max(100),
  sizeBytes: z.number().int().positive(),
  statedAmount: claimAmountSchema.nullable().optional(),
});

export const proofUploadTicketSchema = z.object({
  proofId: uuidSchema,
  /** Where to PUT the bytes. Expires; see `expiresInSeconds`. */
  uploadUrl: z.string(),
  /** Headers the PUT must send for the signature to match. */
  requiredHeaders: z.record(z.string()),
  expiresInSeconds: z.number().int().positive(),
  maxSizeBytes: z.number().int().positive(),
});
export type ProofUploadTicket = z.infer<typeof proofUploadTicketSchema>;

/**
 * STEP 3. The client says it finished; the server verifies the object EXISTS, and that its real
 * size and content type are the ones it authorised, before the row becomes `stored` and the
 * document starts counting as evidence. Without this a client could take a ticket and never upload,
 * leaving a proof that looks attached and is not there.
 */
export const confirmProofUploadBodySchema = z.object({ proofId: uuidSchema });

export const proofLinkSchema = z.object({
  url: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  expiresInSeconds: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// HR verification queue
// ---------------------------------------------------------------------------

export const verificationQueueQuerySchema = z.object({
  legalEntityId: uuidSchema,
  fiscalYear: z.coerce.number().int().min(2000).max(2100),
  /** A section code from the configuration, or nothing for all of them. */
  sectionCode: z.string().max(20).optional(),
  departmentId: uuidSchema.optional(),
  state: taxDeclarationStateSchema.optional(),
  /** Name or employee code. */
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(10).max(100).default(25),
});

/** One row of the queue: an ITEM, because verification happens per category, not per employee. */
export const verificationQueueRowSchema = z.object({
  declarationId: uuidSchema,
  declarationItemId: uuidSchema,
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
  regime: taxRegimeSchema,
  revisionNo: z.number().int().positive(),
  sectionCode: z.string(),
  categoryLabel: z.string(),
  declaredAmount: decimalStringSchema,
  evidenceAmount: decimalStringSchema.nullable(),
  verifiedAmount: decimalStringSchema.nullable(),
  currency: currency,
  documentCount: z.number().int().nonnegative(),
  /** Set for a `payroll_sourced` category: the evidence is the payslip, not an upload. */
  isPayrollSourced: z.boolean(),
  itemState: z.enum([
    'submitted',
    'verified',
    'partially_verified',
    'disallowed',
    'clarification_required',
  ]),
  submittedAt: instantSchema.nullable(),
  /**
   * False when this row is the reviewer's own declaration. The row is still listed — hiding it
   * would look like missing data — but it cannot be acted on. See can.ts's SELF_EXCLUDED_PERMISSIONS.
   */
  canVerify: z.boolean(),
});
export type VerificationQueueRow = z.infer<typeof verificationQueueRowSchema>;

export const verificationQueueSchema = z.object({
  items: z.array(verificationQueueRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  taxYear: taxYearSchema,
  summary: z.object({
    proofsSubmitted: z.number().int().nonnegative(),
    eligibleEmployees: z.number().int().nonnegative(),
    submittedEmployees: z.number().int().nonnegative(),
    itemsVerified: z.number().int().nonnegative(),
    itemsPending: z.number().int().nonnegative(),
    verifiedAmount: decimalStringSchema,
    currency: currency,
  }),
});
export type VerificationQueue = z.infer<typeof verificationQueueSchema>;

/** Everything the review drawer shows, in one request. */
export const verificationDetailSchema = z.object({
  declarationItemId: uuidSchema,
  declarationId: uuidSchema,
  employmentId: uuidSchema,
  personName: z.string(),
  employeeCode: z.string(),
  designation: z.string().nullable(),
  departmentName: z.string().nullable(),
  fiscalYear: fiscalYearSchema,
  fiscalYearLabel: z.string(),
  regime: taxRegimeSchema,
  revisionNo: z.number().int().positive(),
  item: declarationItemSchema,
  /** The category's rules as configured for this year — the cap the reviewer is working against. */
  category: taxCategorySchema,
  section: taxSectionSchema,
  /** What is already accepted elsewhere in the same section, so a shared cap is visible. */
  sectionAlreadyVerified: decimalStringSchema,
  /** Every review ever recorded on this item, newest first. */
  reviewHistory: z.array(
    z.object({
      id: uuidSchema,
      revisionNo: z.number().int().positive(),
      decision: taxProofDecisionSchema,
      acceptedAmount: decimalStringSchema,
      remarks: z.string().nullable(),
      reviewedByName: z.string().nullable(),
      reviewedAt: instantSchema,
    }),
  ),
  canVerify: z.boolean(),
  cannotVerifyReason: z.string().nullable(),
});
export type VerificationDetail = z.infer<typeof verificationDetailSchema>;

/**
 * One reviewer's decision.
 *
 * REMARKS ARE MANDATORY for anything short of full acceptance — refused here rather than merely
 * discouraged in the UI, because "why was my 80D claim cut to ₹50,000" is the question this field
 * exists to answer, and an empty one makes the decision unexplainable six months later.
 */
export const verifyProofBodySchema = z
  .object({
    /** Rejects a decision written against an item another reviewer has already changed. */
    expectedVersion: z.number().int().positive(),
    decision: taxProofDecisionSchema,
    acceptedAmount: claimAmountSchema,
    remarks: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((b, ctx) => {
    const needsRemarks = b.decision !== 'accepted';
    if (needsRemarks && !b.remarks?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          'Say why: a partial acceptance, a disallowance or a request for clarification has to be explainable to the employee',
        path: ['remarks'],
      });
    }
    if (b.decision === 'disallowed' && Number.parseFloat(b.acceptedAmount) !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A disallowed claim accepts nothing',
        path: ['acceptedAmount'],
      });
    }
  });

// ---------------------------------------------------------------------------
// Regime opt-in summary
// ---------------------------------------------------------------------------

export const regimeSummarySchema = z.object({
  taxYear: taxYearSchema,
  totalEmployees: z.number().int().nonnegative(),
  counts: z.array(
    z.object({
      regime: taxRegimeSchema,
      employees: z.number().int().nonnegative(),
      /** Employees on this regime who never elected — it is the configured default for them. */
      byDefault: z.number().int().nonnegative(),
      /** A percentage of `totalEmployees`, as a decimal string. */
      sharePercent: decimalStringSchema,
    }),
  ),
  notElected: z.number().int().nonnegative(),
  declarationsSubmitted: z.number().int().nonnegative(),
  declarationsVerified: z.number().int().nonnegative(),
  byDepartment: z.array(
    z.object({
      departmentId: uuidSchema.nullable(),
      departmentName: z.string(),
      employees: z.number().int().nonnegative(),
      oldRegime: z.number().int().nonnegative(),
      newRegime: z.number().int().nonnegative(),
    }),
  ),
});
export type RegimeSummary = z.infer<typeof regimeSummarySchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const payrollTaxContract = c.router(
  {
    // ---- Tax-year configuration ------------------------------------------------
    getTaxYear: {
      method: 'GET',
      path: '/years',
      query: z.object({
        legalEntityId: uuidSchema,
        fiscalYear: z.coerce.number().int().min(2000).max(2100),
      }),
      responses: {
        200: taxYearSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        /** No published configuration for that entity and year — declarations cannot open. */
        404: errorBodySchema,
      },
      summary: 'The published tax-year configuration: categories, caps, and the submission window',
      metadata: routeMeta(['payroll.tax:read', 'payroll:read']),
    },

    upsertTaxYear: {
      method: 'PUT',
      path: '/years',
      headers: idempotencyHeadersSchema,
      body: upsertTaxYearBodySchema,
      responses: {
        200: taxYearSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: "Save a new version of a fiscal year's tax configuration",
      metadata: routeMeta('payroll.tax.config:manage'),
    },

    // ---- The employee's own declaration ---------------------------------------
    getMyDeclaration: {
      method: 'GET',
      path: '/me',
      query: z.object({ fiscalYear: z.coerce.number().int().min(2000).max(2100).optional() }),
      responses: {
        200: myDeclarationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'My regime, my declaration, and what I may claim this year',
      metadata: routeMeta('payroll.tax.own:manage'),
    },

    getRegimeComparison: {
      method: 'GET',
      path: '/me/regime-comparison',
      query: z.object({ fiscalYear: z.coerce.number().int().min(2000).max(2100).optional() }),
      responses: {
        200: regimeComparisonSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'An estimate of my tax under each regime, to inform the election',
      metadata: routeMeta('payroll.tax.own:manage'),
    },

    electRegime: {
      method: 'POST',
      path: '/me/regime',
      body: electRegimeBodySchema,
      responses: {
        200: taxElectionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Elect my income-tax regime for a fiscal year (writes a new revision)',
      metadata: routeMeta('payroll.tax.own:manage'),
    },

    saveMyDeclaration: {
      method: 'PUT',
      path: '/me/declaration',
      body: saveDeclarationBodySchema,
      responses: {
        200: myDeclarationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Stale `expectedVersion`, or the revision is no longer a draft. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Save my draft declaration items',
      metadata: routeMeta('payroll.tax.own:manage'),
    },

    submitMyDeclaration: {
      method: 'POST',
      path: '/me/declaration/submit',
      headers: idempotencyHeadersSchema,
      body: submitDeclarationBodySchema,
      responses: {
        200: myDeclarationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Submit my declaration as an immutable revision',
      metadata: routeMeta('payroll.tax.own:manage'),
    },

    reviseMyDeclaration: {
      method: 'POST',
      path: '/me/declaration/revise',
      headers: idempotencyHeadersSchema,
      body: reviseDeclarationBodySchema,
      responses: {
        200: myDeclarationSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Open a new revision to correct a submitted declaration',
      metadata: routeMeta('payroll.tax.own:manage'),
    },

    // ---- Proofs ----------------------------------------------------------------
    requestProofUpload: {
      method: 'POST',
      path: '/proofs/upload-ticket',
      body: requestProofUploadBodySchema,
      responses: {
        201: proofUploadTicketSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        /** Content type not allowed, or the size exceeds the server's limit. */
        422: errorBodySchema,
      },
      summary: 'Authorise one private proof upload and mint a short-lived PUT URL',
      metadata: routeMeta('payroll.tax.own:manage', { ai: 'hidden' }),
    },

    confirmProofUpload: {
      method: 'POST',
      path: '/proofs/confirm',
      body: confirmProofUploadBodySchema,
      responses: {
        200: declarationProofSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** The object is not there, or is not the size and type that were authorised. */
        422: errorBodySchema,
      },
      summary: 'Verify the uploaded object and accept it as evidence',
      metadata: routeMeta('payroll.tax.own:manage', { ai: 'hidden' }),
    },

    getProofLink: {
      method: 'GET',
      path: '/proofs/:proofId/link',
      pathParams: z.object({ proofId: uuidSchema }),
      responses: {
        200: proofLinkSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Mint a short-lived authorised link to one proof',
      metadata: routeMeta(null),
    },

    deleteProof: {
      method: 'DELETE',
      path: '/proofs/:proofId',
      pathParams: z.object({ proofId: uuidSchema }),
      responses: {
        204: z.null(),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        /** Its revision is already submitted — correct it by opening a new revision. */
        409: errorBodySchema,
      },
      summary: 'Remove a proof from a draft revision',
      metadata: routeMeta('payroll.tax.own:manage'),
    },

    // ---- HR verification -------------------------------------------------------
    getVerificationQueue: {
      method: 'GET',
      path: '/verification',
      query: verificationQueueQuerySchema,
      responses: {
        200: verificationQueueSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The proof-verification queue for one entity and fiscal year',
      metadata: routeMeta('payroll.tax:read'),
    },

    getVerificationDetail: {
      method: 'GET',
      path: '/verification/:declarationItemId',
      pathParams: z.object({ declarationItemId: uuidSchema }),
      responses: {
        200: verificationDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'One declaration item, its documents, its caps and its review history',
      metadata: routeMeta(null),
    },

    verifyProof: {
      method: 'POST',
      path: '/verification/:declarationItemId/decision',
      pathParams: z.object({ declarationItemId: uuidSchema }),
      body: verifyProofBodySchema,
      responses: {
        200: verificationDetailSchema,
        401: errorBodySchema,
        /** Includes the reviewer's own declaration — never verifiable by them. */
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Record a verification decision on one declaration item',
      metadata: routeMeta('payroll.tax.proof:verify'),
    },

    getRegimeSummary: {
      method: 'GET',
      path: '/regime-summary',
      query: z.object({
        legalEntityId: uuidSchema,
        fiscalYear: z.coerce.number().int().min(2000).max(2100),
      }),
      responses: {
        200: regimeSummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Regime opt-in counts for one entity and fiscal year',
      metadata: routeMeta('payroll.tax:read'),
    },
  },
  { pathPrefix: '/payroll/tax' },
);
